import _ from "lodash";
import {
  ArrowDownLeftIcon,
  ArrowLeftRightIcon,
  ArrowRightLeftIcon,
  ArrowUpRightIcon,
  BuildingIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  DollarSignIcon,
  FileTextIcon,
  GitBranchIcon,
  LandmarkIcon,
  ListChecksIcon,
  TableIcon,
  TagsIcon,
  TextAlignStartIcon,
  WalletIcon,
} from "lucide-react";
import { BudgetLinesOfRecord } from "@/objects/BudgetLineObject";

import {
  JournalEntryStateEnum,
  TagColorEnum,
  TransactionFragment,
} from "@/__generated__/graphql";
import { TableColumnType } from "@/components/@types";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { FieldTable } from "@/components/organisms/Field";
import {
  useNodeField,
  useNodeLayoutStore,
} from "@/components/organisms/Node/Node.hooks";
import { Queries } from "@/constants";
import { EnumColors, TagColors, Values } from "@/constants/values";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { iconsByTypename } from "@/objects/@icons";
import {
  ObjectCardProps,
  ObjectCommandContext,
  ObjectType,
} from "@/objects/@types";
import { journalEntryStateColor } from "@/objects/JournalEntryObject";
import { Utils } from "@/utils";
import { getReferenceValueOptionsByTypename } from "@/objects";
import type { JournalEntryLine } from "@/objects/JournalEntryObject";
import type { SelectEntityContextType } from "@/app/app/_/commands/SelectEntityCommand";
import { State } from "@/state";
import { CommandDefinitionType } from "@/state/@types";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { gql } from "@apollo/client";

const colorCircleIconCache = new Map<string, React.FC>();

function getColorCircleIcon(color?: TagColorEnum | null): React.FC {
  const solidColor = color ? TagColors[color]?.solidColor : undefined;
  const key = solidColor ?? "default";
  let icon = colorCircleIconCache.get(key);
  if (!icon) {
    const bgColor = solidColor ?? "var(--muted-foreground)";
    icon = () => (
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: bgColor,
          opacity: 0.6,
        }}
      />
    );
    colorCircleIconCache.set(key, icon);
  }
  return icon;
}

/**
 * What a transaction was in another currency, as its bank's statement prints
 * it, with the rate between the two. Empty where it was in none.
 */
function describeOriginalAmount(data?: TransactionFragment | null): string {
  if (data?.amountOriginal == null || !data.amountOriginalCurrency) {
    return "";
  }
  return `${Utils.fCurrency(Math.abs(Number(data.amountOriginal)), false, true, data.amountOriginalCurrency)} · 1 ${data.amountOriginalCurrency} = ${Number(data.amountOriginalToAmountFx).toFixed(4)} ${data.amountCurrency ?? ""}`.trim();
}

function getTransactionCommands(
  ctx: ObjectCommandContext<TransactionFragment>,
): CommandDefinitionType[] {
  const { data, entity, updateNode } = ctx;
  if (!data || !entity) return [];

  const commands: CommandDefinitionType[] = [];
  const group = data.description || "Untitled";

  const tagGroupId = entity.tagGroups?.[0]?.id;
  if (tagGroupId) {
    commands.push({
      id: `node-manage-tags-${data.id}`,
      title: "Manage Tags",
      group,
      groupPriority: 1,
      isContextual: true,
      onClick: () => {
        State.getState().pushToCommandStack({
          commandDefinitionId: "manage-tags",
          context: {
            nodeId: tagGroupId,
            entityIdentifier: entity.identifier,
          },
        });
      },
    });
  }

  for (const tag of entity.tagGroups?.[0]?.tags ?? []) {
    const tagName = tag.name ?? "Unnamed";
    // ``data`` is whatever the node store holds right now, which on a detail
    // page is a stub carrying only the id until the record lands (and stays
    // that way if the request fails). Commands are registered from that first
    // render, so nothing here may assume a fully loaded node.
    const assignedTags = data.tags ?? [];
    const isAssigned = assignedTags.some(
      (t) => t.identifier === tag.identifier,
    );
    const action = isAssigned ? "Remove" : "Add";
    commands.push({
      id: `node-tag-${data.id}-${tag.identifier}`,
      title: (
        <>
          <span style={{ color: "var(--muted-foreground)" }}>
            {action} Tag &gt;
          </span>{" "}
          <span>{tagName}</span>
        </>
      ),
      searchText: `${action} Tag ${tagName}`,
      group,
      groupPriority: 1,
      isContextual: true,
      ReactIcon: getColorCircleIcon(tag.color) as any,
      onClick: () => {
        const newTags = isAssigned
          ? assignedTags
              .filter((t) => t.identifier !== tag.identifier)
              .map((t) => ({ identifier: t.identifier }))
          : [
              ...assignedTags.map((t) => ({ identifier: t.identifier })),
              { identifier: tag.identifier },
            ];
        updateNode({
          variables: {
            input: {
              id: data.id,
              node: {
                transaction: {
                  tags: { nodes: newTags },
                },
              },
            },
          },
        });
        State.getState().popCommandsFromStack(
          State.getState().commandInstanceStack.length,
        );
      },
    });
  }

  // Moved to another business, or come from one: said already. Only a bank
  // payment is moved.
  const hasTransfer = !!data?.belongsToEntity || !!data?.cameFromEntity;
  const isPosted =
    data?.journalEntry?.journalEntryState === JournalEntryStateEnum.Posted;
  if (!hasTransfer && !isPosted && data?.financialAccount) {
    commands.push({
      id: `node-mark-belongs-to-${data.id}`,
      title: "Mark belongs to",
      group,
      groupPriority: 1,
      isContextual: true,
      ReactIcon: ArrowUpRightIcon,
      onClick: () => {
        const currentEntityIdentifier = data?.entity?.identifier;
        State.getState().pushToCommandStack({
          commandDefinitionId: "select-entity",
          context: {
            filterOptions: ((
              options: Array<{ identifier?: string; name?: string }>,
              filter: string,
            ) =>
              options.filter(
                (option) =>
                  option?.identifier !== currentEntityIdentifier &&
                  (filter === "" ||
                    option?.name
                      ?.toLowerCase()
                      .includes(filter.toLowerCase()) ||
                    option?.identifier
                      ?.toLowerCase()
                      .includes(filter.toLowerCase())),
              )) as SelectEntityContextType["filterOptions"],
            onSubmit: ((selectedEntity: { identifier: string }) => {
              updateNode({
                variables: {
                  input: {
                    id: data.id,
                    node: {
                      transaction: {
                        belongsToEntity: {
                          identifier: selectedEntity.identifier,
                        },
                      },
                    },
                  },
                },
              });
              State.getState().clearCommandStack();
            }) as SelectEntityContextType["onSubmit"],
          },
        });
      },
    });
  }

  for (const cat of entity.categories ?? []) {
    commands.push({
      id: `node-categorize-${data.id}-${cat.identifier}`,
      title: `Categorize \u203A ${cat.name ?? "Unnamed"}`,
      group,
      groupPriority: 1,
      isContextual: true,
      ReactIcon: getColorCircleIcon(cat.color) as any,
      onClick: () => {
        updateNode({
          variables: {
            input: {
              id: data.id,
              node: {
                transaction: {
                  categoryObj: { identifier: cat.identifier },
                },
              },
            },
          },
        });
        State.getState().popCommandsFromStack(
          State.getState().commandInstanceStack.length,
        );
      },
    });
  }

  return commands;
}

function TransactionJournalEntryTab({ size }: { size: "sm" | "md" }) {
  const store = useNodeLayoutStore();
  const { fieldDefinition, fieldEvents, fieldValues } = useNodeField(
    store,
    "journalEntryLines",
  );
  const journalEntryId = (fieldValues as TransactionFragment)?.journalEntry?.id;
  // Display-only: the lines render straight from the query, the transaction's
  // own node store is not hydrated with them.
  const { data, loading } = useBackendQuery(Queries.JOURNAL_ENTRY_LINES_QUERY, {
    variables: { id: journalEntryId! },
    skip: !journalEntryId,
    fetchPolicy: "cache-first",
  });

  if (!fieldDefinition || fieldDefinition.type !== "table") {
    return null;
  }

  const journalEntry =
    data?.node?.__typename === "JournalEntryObject" ? data.node : null;
  const value: JournalEntryLine[] = journalEntry?.transactions ?? [];
  const given = (fieldDefinition.getValueOptions?.(fieldValues) ?? {
    columns: [],
  }) as { columns: TableColumnType[] };
  // What a line was on its document in another currency, only where one was -
  // every column a share of the table still.
  const shown = given.columns.filter(
    (column) =>
      column.objectKey !== "amountOriginal" ||
      value.some((line) => line.amountOriginal != null),
  );
  const totalWidth = shown.reduce(
    (sum, column) => sum + Number(column.width ?? 0),
    0,
  );
  const valueOptions = {
    columns: shown.map((column) => ({
      ...column,
      width: (Number(column.width ?? 0) * 100) / (totalWidth || 100),
    })),
  };

  return (
    <div className="flex-1 min-h-0 w-full max-w-full flex flex-col overflow-hidden">
      <FieldTable
        value={value}
        valueOptions={valueOptions}
        events={fieldEvents}
        cellSize={size}
        isLoading={loading && value.length === 0}
        style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
      />
    </div>
  );
}

/**
 * A transaction as a card: how much and when, from which account, and where
 * it went - its category and whether its entry is posted.
 */
function TransactionCard({
  data,
  isLoading = false,
}: ObjectCardProps<TransactionFragment>) {
  const amount =
    data?.amount != null && data.amount !== ""
      ? Utils.fCurrency(data.amount, false, true)
      : null;
  const date = data?.effectiveDate ? new Date(data.effectiveDate) : null;
  const state = data?.journalEntry?.journalEntryState;

  return (
    <div className="flex flex-col gap-1.5 h-full min-h-0">
      {amount && (
        <Typography
          level="h4"
          className="[line-height:1.1] [font-variant-numeric:tabular-nums]"
        >
          <Skeleton loading={isLoading} variant="inline">
            {amount}
          </Skeleton>
        </Typography>
      )}

      {(date || data?.financialAccount?.name) && (
        <Typography level="body-sm" className="truncate text-muted-foreground">
          <Skeleton loading={isLoading} variant="inline">
            {[date ? Utils.fDate(date) : null, data?.financialAccount?.name]
              .filter(Boolean)
              .join(" · ")}
          </Skeleton>
        </Typography>
      )}

      <div className="mt-auto flex gap-1 flex-wrap">
        {data?.categoryObj?.name && (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <SimpleEnum
              label={data.categoryObj.name}
              enumColor={
                data.categoryObj.color
                  ? TagColors[data.categoryObj.color]
                  : EnumColors.GREY_1
              }
              size="sm"
            />
          </Skeleton>
        )}
        {state && (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <SimpleEnum
              label={_.startCase(state.toString().toLowerCase())}
              enumColor={journalEntryStateColor(state)}
              size="sm"
            />
          </Skeleton>
        )}
      </div>
    </div>
  );
}

const object: ObjectType<TransactionFragment> = {
  humanName: "transaction",
  typename: "TransactionObject",
  report: {
    source: "transactions",
    description: "Every payment in and out, line by line",
    asks: "What did we spend at each merchant last quarter?",
  },
  ReactIcon: ArrowLeftRightIcon,
  isEntity: false,
  getCommands: getTransactionCommands,
  getActions: (data) => {
    const actions = [];
    return actions;
  },
  getEntityId: (data) => {
    return data?.entity?.id;
  },
  titleFieldKey: "description",
  getTitle: (data) => {
    return data?.description;
  },
  Card: TransactionCard,
  placeholder: {
    __typename: "TransactionObject",
    id: btoa("TransactionObject:placeholder"),
    identifier: "placeholder",
    description: "AMAZON MKTPLACE PMTS",
    amount: "-48.10",
    effectiveDate: "2026-01-15",
    tags: [],
    financialAccount: {
      id: btoa("FinancialAccountObject:placeholder"),
      identifier: "placeholder",
      name: "Visa Infinite",
    },
    categoryObj: {
      id: btoa("ChartedAccountObject:placeholder"),
      identifier: "placeholder",
      name: "Office supplies",
      isCategory: true,
      entity: {
        id: btoa("EntityObject:placeholder"),
        identifier: "placeholder",
      },
    },
    journalEntry: {
      id: btoa("JournalEntryObject:placeholder"),
      identifier: "placeholder",
      journalEntryState: JournalEntryStateEnum.Posted,
    },
    entity: {
      id: btoa("EntityObject:placeholder"),
      identifier: "placeholder",
      name: "Elm Street Coffee",
    },
  } as TransactionFragment,
  groups: [
    {
      id: "overview",
      label: "Overview",
      isVisible: true,
    },
    {
      id: "journalEntry",
      label: "Journal Entry",
      isVisible: (data) => !!data?.journalEntry,
      component: TransactionJournalEntryTab,
      flush: true,
      card: {
        isLabelVisible: true,
        flex: 1,
        isFlush: true,
      },
    },
  ],
  getReferenceValueOptions(entityIdentifiers) {
    return {
      isNullable: false,
      query: gql`
        query TransactionList($entityIdentifiers: [String!]) {
          transactions(entityIdentifiers: $entityIdentifiers) {
            data {
              ...Transaction
            }
          }
        }
      `,
      queryOptions: {
        variables: {
          entityIdentifiers: entityIdentifiers,
        },
      },
      onRenderOption: (option: TransactionFragment) => {
        return option.identifier;
      },
      filterOptions: (options: TransactionFragment[], filter: string) => {
        return options.filter((option) =>
          option.identifier.toLowerCase().includes(filter.toLowerCase()),
        );
      },
    };
  },
  fieldDefinitions: [
    {
      objectKey: "budgetLines",
      label: "Budget",
      type: "custom",
      ReactIcon: WalletIcon,
      asString: () => "",
      getValue: (data) => () => <BudgetLinesOfRecord id={data?.id} />,
      updateProps: {
        isRequired: false,
        isEditable: false,
        isVisible: (data) =>
          Values.IS_BUDGET_SHOWN && Boolean(data?.journalEntry?.id),
      },
      summaryProps: { isVisible: false },
    },
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entity?.identifier,
      getValue: (data) => () => {
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BoxForEntity
              identifier={data?.entity?.identifier}
              representation="character"
            />
          </div>
        );
      },
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Transaction",
      type: "reference",
      ReactIcon: iconsByTypename.TransactionObject,
      asString: (data) => data?.identifier,
      getValue: (data) => {
        return {
          __typename: "TransactionObject",
          name: data?.description,
          id: data?.id,
          identifier: data?.identifier,
          entityId: data?.entity?.id,
        };
      },
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("TransactionObject")([
          data?.entity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "description",
      label: "Description",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.description,
      getValue: (data) => {
        return data?.description;
      },
      getValueOptions: () => ({
        placeholder: "Invoice payment made in cash, etc.",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
    },
    {
      objectKey: "notes",
      label: "Notes",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.notes ?? "",
      getValue: (data) => data?.notes ?? "",
      getValueOptions: () => ({
        placeholder: "Add notes...",
      }),
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
    },
    {
      objectKey: "effectiveDate",
      inputKey: "effectiveDate",
      // The day the books go by: the day it posted, where the bank says.
      label: "Posting date",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      asString: (data) => data?.effectiveDate,
      getValue: (data) => {
        return data?.effectiveDate;
      },
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "currency",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "occurredOn",
      // The day it was made - the card swiped - where the bank or statement says.
      label: "Transaction date",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      asString: (data) => data?.occurredOn ?? "",
      getValue: (data) => data?.occurredOn,
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
    },
    {
      objectKey: "amount",
      // Handed its currency, to say it where it is not its business's.
      linkedValuesKeys: ["amount", "amountCurrency"],
      label: "Amount",
      type: "currency",
      ReactIcon: DollarSignIcon,
      asString: (data) => data?.amount,
      getValue: (data) => {
        return data?.amount;
      },
      // In another currency than its business's - a bank account in US
      // dollars - its sign says which.
      getValueOptions: (data) => ({
        currency:
          data?.amountCurrency &&
          data?.entity?.currency &&
          data.amountCurrency !== data.entity.currency
            ? data.amountCurrency
            : null,
      }),
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "currency",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // What it was in another currency: a purchase abroad, as its bank's
      // statement prints it, with the rate between the two.
      objectKey: "amountOriginal",
      // Handed its currency and rate, and the payment's own currency.
      linkedValuesKeys: [
        "amountOriginal",
        "amountOriginalCurrency",
        "amountOriginalToAmountFx",
        "amountCurrency",
      ],
      label: "Original",
      type: "text",
      ReactIcon: ArrowRightLeftIcon,
      asString: (data) => describeOriginalAmount(data),
      getValue: (data) => describeOriginalAmount(data),
      updateProps: {
        isRequired: false,
        isEditable: false,
        isVisible: (data) => Boolean(describeOriginalAmount(data)),
      },
      summaryProps: {
        isVisible: (data) => Boolean(describeOriginalAmount(data)),
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "tags",
      inputKey: "tags",
      label: "Tags",
      type: "references",
      ReactIcon: TagsIcon,
      asString: (data) =>
        (data?.tags && data?.tags?.length > 0
          ? data?.tags?.map((tag) => tag.name).join(", ")
          : "No tags") || "No tags",
      getValue: (data) => {
        return (data?.tags || []).map((tag) => ({
          __typename: "TagObject",
          name: tag.name,
          id: tag.id,
          identifier: tag.identifier,
          color: tag.color,
        }));
      },
      getValueOptions: (data) =>
        getReferenceValueOptionsByTypename("TagObject")([
          data?.entity?.identifier,
        ]),
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: true,
        getEditableObjectInfo: (_, entity) => ({
          typename: "TagGroupObject",
          id: entity?.tagGroups?.[0]?.id,
          identifier: entity?.tagGroups?.[0]?.identifier,
        }),
      },
    },
    {
      objectKey: "categoryObj",
      inputKey: "categoryObj",
      label: "Category",
      type: "reference",
      ReactIcon: TagsIcon,
      asString: (data) => data?.categoryObj?.name || "No category",
      getValue: (data) => {
        if (!data?.categoryObj) {
          return {
            __typename: "CategoryObject",
            name: null,
            id: null,
            identifier: null,
          };
        }
        return {
          __typename: "CategoryObject",
          name: data.categoryObj.name,
          id: data.categoryObj.id,
          identifier: data.categoryObj.identifier,
          color: data.categoryObj.color,
        };
      },
      getValueOptions: (data) => ({
        isNullable: true,
        ...getReferenceValueOptionsByTypename("CategoryObject")([
          data?.entity?.identifier,
        ]),
      }),
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: true,
        isEditable: true,
        // Its categories are edited together, as accounts under those they
        // narrow, in Manage categories.
        getEditableObjectInfo: (_, entity) => ({
          typename: "",
          id: entity?.id,
          identifier: entity?.identifier,
          commandDefinitionId: "manage-categories",
        }),
      },
    },
    {
      objectKey: "financialAccount",
      label: "Account",
      type: "reference",
      ReactIcon: LandmarkIcon,
      asString: (data) =>
        data?.financialAccount?.name ||
        data?.financialAccount?.accountNumber ||
        "",
      getValue: (data) => {
        if (!data?.financialAccount) {
          return {
            __typename: "FinancialAccountObject",
            name: null,
            id: null,
            identifier: null,
          };
        }
        return {
          __typename: "FinancialAccountObject",
          name:
            data.financialAccount.name ||
            data.financialAccount.accountNumber ||
            null,
          id: data.financialAccount.id,
          identifier: data.financialAccount.identifier,
        };
      },
      getValueOptions: (data) => ({
        isNullable: true,
        ...getReferenceValueOptionsByTypename("FinancialAccountObject")([
          data?.entity?.identifier,
        ]),
      }),
      updateProps: {
        isRequired: false,
        // Source-of-truth lives on the bank import / Plaid sync — letting
        // a user re-point a transaction at a different account would
        // desync the statement document and the journal entry, so this
        // is read-only at the row level for now.
        isEditable: false,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: true,
      },
    },
    {
      objectKey: "paymentFor",
      inputKey: "paymentFor",
      label: "Payment for",
      type: "references",
      ReactIcon: iconsByTypename.DocumentObject,
      asString: (data) =>
        (data?.paymentFor && data.paymentFor.length > 0
          ? data.paymentFor.map((document) => document.name).join(", ")
          : "") || "",
      getValue: (data) =>
        (data?.paymentFor || []).map((document) => ({
          __typename: "DocumentObject",
          name: document.name,
          id: document.id,
          identifier: document.identifier,
        })),
      getValueOptions: (data) => ({
        isNullable: true,
        ...getReferenceValueOptionsByTypename("DocumentObject")([
          data?.entity?.identifier,
        ]),
      }),
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
    },
    {
      objectKey: "lineItemOn",
      label: "Line item on",
      type: "reference",
      ReactIcon: FileTextIcon,
      asString: (data) => data?.lineItemOn?.name || "",
      getValue: (data) => {
        if (!data?.lineItemOn) {
          return {
            __typename: "DocumentObject",
            name: null,
            id: null,
            identifier: null,
          };
        }
        return {
          __typename: "DocumentObject",
          name: data.lineItemOn.name,
          id: data.lineItemOn.id,
          identifier: data.lineItemOn.identifier,
        };
      },
      getValueOptions: (data) => ({
        isNullable: true,
        ...getReferenceValueOptionsByTypename("DocumentObject")([
          data?.entity?.identifier,
        ]),
      }),
      updateProps: {
        isRequired: false,
        isEditable: false,
        isVisible: (data) => !!data?.lineItemOn,
      },
    },
    {
      // The other business it is for, read as said; set as the business it
      // belongs to.
      objectKey: "belongsToEntity",
      inputKey: "belongsToEntity",
      label: "Belongs to",
      type: "reference",
      ReactIcon: ArrowUpRightIcon,
      asString: (data) => data?.belongsToEntity?.name || "",
      getValue: (data) => {
        return {
          __typename: "EntityObject",
          name: data?.belongsToEntity?.name || null,
          id: data?.belongsToEntity?.id || null,
          identifier: data?.belongsToEntity?.identifier || null,
        };
      },
      getValueOptions: (data) => ({
        isNullable: true,
        ...getReferenceValueOptionsByTypename("EntityObject")([]),
        filterOptions: (
          options: Array<{ identifier?: string; name?: string }>,
          filter: string,
        ) => {
          // filter out the current transaction's entity
          return options.filter(
            (option) =>
              option?.identifier !== data?.entity?.identifier &&
              (option?.name?.toLowerCase().includes(filter.toLowerCase()) ||
                option?.identifier
                  ?.toLowerCase()
                  .includes(filter.toLowerCase())),
          );
        },
      }),
      updateProps: {
        isRequired: false,
        isEditable: (data) => {
          // A bank payment, not moved already and not posted.
          const hasTransfer = !!data?.belongsToEntity || !!data?.cameFromEntity;
          const isPosted =
            data?.journalEntry?.journalEntryState ===
            JournalEntryStateEnum.Posted;
          return !hasTransfer && !isPosted && !!data?.financialAccount;
        },
      },
      summaryProps: {
        isVisible: false,
      },
      // fieldProps: {
      //   headerFieldType: "reference",
      //   isSortable: false,
      //   isFilterable: false,
      // },
    },
    {
      objectKey: "cameFromEntity",
      label: "Came from",
      type: "reference",
      ReactIcon: ArrowDownLeftIcon,
      asString: (data) => data?.cameFromEntity?.name || "",
      getValue: (data) => {
        return {
          __typename: "EntityObject",
          name: data?.cameFromEntity?.name || null,
          id: data?.cameFromEntity?.id || null,
          identifier: data?.cameFromEntity?.identifier || null,
        };
      },
      getValueOptions: () => ({
        isNullable: true,
        ...getReferenceValueOptionsByTypename("EntityObject")([]),
      }),
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: false,
      },
      // fieldProps: {
      //   headerFieldType: "reference",
      //   isSortable: false,
      //   isFilterable: false,
      // },
    },
    {
      objectKey: "journalEntryLines",
      label: "Journal Entry",
      type: "table",
      ReactIcon: TableIcon,
      groupId: "journalEntry",
      linkedValuesKeys: ["journalEntry", "entity"],
      asString: () => "",
      getValue: () => [],
      getValueOptions: (data) => ({
        // Every column a share of the table, the shares adding up to all of it.
        columns: [
          {
            label: "Description",
            type: "string",
            width: 28,
            widthMode: "percentage",
            headerFieldType: "string",
            objectKey: "description",
            isDisabled: false,
            isSearchable: false,
            isMultiline: true,
            getValue: (line: JournalEntryLine) => line?.description || "",
            getValueOptions: () => ({
              placeholder: "Cash payment for invoice 123",
            }),
          },
          {
            label: "Account",
            type: "reference",
            width: 28,
            widthMode: "percentage",
            headerFieldType: "string",
            objectKey: "chartedAccount",
            isDisabled: false,
            isSearchable: false,
            getValue: (line: JournalEntryLine) => ({
              __typename: "ChartedAccountObject",
              id: line?.chartedAccount?.id,
              identifier: line?.chartedAccount?.identifier,
              name: line?.chartedAccount?.name,
            }),
            getValueOptions: () =>
              getReferenceValueOptionsByTypename("ChartedAccountObject")([
                data?.entity?.identifier,
              ]),
          },
          {
            label: "Document",
            type: "reference",
            width: 12,
            widthMode: "percentage",
            headerFieldType: "string",
            objectKey: "document",
            inputKey: "paymentFor",
            isDisabled: false,
            isSearchable: false,
            getValue: (
              line: JournalEntryLine & {
                document?: JournalEntryLine["paymentFor"][number] | null;
              },
            ) => {
              const document = line.document ?? line.paymentFor?.[0] ?? null;
              if (!document) {
                return {
                  __typename: "DocumentObject",
                  name: null,
                  id: null,
                  identifier: null,
                };
              }
              return {
                __typename: "DocumentObject",
                name: document.name || document.reference || "",
                id: document.id,
                identifier: document.identifier,
              };
            },
            getValueOptions: () => ({
              isNullable: true,
              ...getReferenceValueOptionsByTypename("DocumentObject")([
                data?.entity?.identifier,
              ]),
            }),
          },
          {
            label: "Tags",
            type: "references",
            width: 8,
            widthMode: "percentage",
            headerFieldType: "references",
            objectKey: "tags",
            isDisabled: false,
            isSearchable: false,
            getValue: (line: JournalEntryLine) =>
              (line?.tags || []).map((tag) => ({
                __typename: "TagObject",
                name: tag.name,
                id: tag.id,
                identifier: tag.identifier,
                color: tag.color,
              })),
            getValueOptions: () =>
              getReferenceValueOptionsByTypename("TagObject")([
                data?.entity?.identifier,
              ]),
          },
          {
            // Converted from a document in another currency: what it was on
            // it, in that currency - shown only where a line was.
            label: "Original",
            type: "currency",
            width: 14,
            widthMode: "percentage",
            headerFieldType: "number",
            objectKey: "amountOriginal",
            isDisabled: true,
            isSearchable: false,
            style: { textAlign: "right" },
            getValue: (line: JournalEntryLine) =>
              line?.amountOriginal != null
                ? String(Math.abs(Number(line.amountOriginal)))
                : "",
            // In its own currency: its sign says which.
            getValueOptions: (line?: unknown) => ({
              currency:
                (line as JournalEntryLine | undefined)
                  ?.amountOriginalCurrency ?? null,
            }),
          },
          {
            label: "Debit",
            type: "currency",
            width: 12,
            widthMode: "percentage",
            headerFieldType: "number",
            objectKey: "debit",
            isDisabled: false,
            isSearchable: false,
            style: { textAlign: "right" },
            getValue: (line: JournalEntryLine) =>
              line?.credit != null ? line?.debit : null,
            getValueOptions: () => ({
              placeholder: "$0",
            }),
          },
          {
            label: "Credit",
            type: "currency",
            width: 12,
            widthMode: "percentage",
            headerFieldType: "number",
            objectKey: "credit",
            isDisabled: false,
            isSearchable: false,
            style: { textAlign: "right" },
            getValue: (line: JournalEntryLine) =>
              line?.debit != null ? line?.credit : null,
            getValueOptions: () => ({
              placeholder: "$0",
            }),
          },
        ],
      }),
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: false,
      },
    },
  ],
};

export { object as TransactionObject };
